/**
 * Auth context object + contract, separated from the AuthProvider component
 * so the provider file exports only a component (react-refresh requirement)
 * and so `vi.mock('@/contexts/useAuth')` in tests cannot shadow the raw
 * context used by direct-useContext consumers.
 *
 * Basename is deliberately distinct from AuthContext.tsx: an `authContext.ts`
 * sibling would collide with extensionless `./AuthContext` resolution on
 * case-insensitive filesystems.
 *
 * Type-only imports keep this module free of runtime side effects (no
 * supabase client initialization from merely importing the context).
 */
import { createContext } from 'react';
import type { Session } from '@supabase/supabase-js';
import type { supabase } from '@/utils/supabase';
import type { ExtendedUser } from '@/types/auth';

export interface AuthContextType {
  user: ExtendedUser | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  supabaseClient: typeof supabase; // Expose the authenticated client
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);
